import { ComponentType } from 'react';
import type { ConfigType } from '@plone/registry';
import { initialBlocks } from './config/initialBlocks';
import installLink from '@fhnw/components/SlateLink/index';
import installEnlargeReduce from '@fhnw/components/EnlargeReduce';
import reducers from './reducers';
import transformers from './transformers';
import { Link } from './customizations/@designsystem/stories/01-core/Link';
import {
  registerLinkComponent,
  LinkComponentProps,
} from '@designsystem/stories/01-core/OptionalLink';

import './config/types';
import { configureWidgets } from './config/widgets';
import {
  configureSettings,
  configureViews,
  configureUtilities,
} from './config/settings';
import { configureBlocks } from './config/blocks';
import { configureComponents } from './config/components';
import { registerGlobalMessages } from './config/messages';

/**
 * Extended configuration type that includes all custom properties
 */
type ExtendedConfigType = ConfigType & {
  experimental: { addBlockButton: { enabled: boolean } };
  settings: {
    slate: {
      toolbarButtons: string[];
      extensions?: any[];
      tableblockExtensions?: any[];
    };
    apiExpanders: any[];
    cookiebotDomainGroupId?: string;
    gaMeasurementId?: string;
  };
};

/**
 * Configures the Volto application for FHNW Web 2025
 *
 * Installs core functionality (EnlargeReduce, Link components), configures
 * the Slate editor with toolbar buttons and extensions, enables experimental
 * features, sets initial blocks, and applies modular configurations for
 * settings, widgets, components, blocks, views, and utilities.
 *
 * @param config - The Volto configuration to be extended
 * @returns The extended configuration
 */
const applyConfig = (config: ExtendedConfigType): ConfigType => {
  /**
   * Registers global messages and link components
   */
  const registerGlobalComponents = () => {
    // Register global translation messages
    registerGlobalMessages();

    // Register custom link component for design system
    registerLinkComponent(Link as ComponentType<LinkComponentProps>);
  };

  /**
   * Installs core FHNW functionality including EnlargeReduce and Link components
   */
  const installCoreFunctionality = () => {
    installEnlargeReduce(config);
    installLink(config);
  };

  /**
   * Configures Redux reducers and applies data transformers
   */
  const configureReducersAndTransformers = () => {
    config.addonReducers = { ...config.addonReducers, ...reducers };
    transformers.forEach((transformer) => {
      transformer();
    });
  };

  /**
   * Configures the Slate editor toolbar by adding the underline button
   */
  const configureSlateToolbar = () => {
    config.settings.slate.toolbarButtons = [
      ...config.settings.slate.toolbarButtons.slice(0, 2),
      'underline',
      ...config.settings.slate.toolbarButtons.slice(2),
    ];
  };

  /**
   * Adds custom extensions to the Slate editor for enhanced data insertion handling
   */
  const addSlateEditorExtensions = () => {
    const {
      insertData,
    } = require('./customizations/@plone/volto-slate/editor/extensions/insertData');

    config.settings.slate.extensions = [
      ...(config.settings.slate.extensions || []),
      insertData,
    ];
  };

  /**
   * Configures extensions specific to table blocks in the Slate editor
   */
  const configureTableBlockExtensions = () => {
    const {
      withTableBlockContext,
    } = require('./customizations/@plone/volto-slate/editor/extensions/withTableBlockContext');
    config.settings.slate.tableblockExtensions = [
      ...(config.settings.slate.tableblockExtensions || []),
      withTableBlockContext,
    ];
  };

  /**
   * Enables experimental features such as the add block button
   */
  const enableExperimentalFeatures = () => {
    config.experimental.addBlockButton.enabled = true;
  };

  /**
   * Sets the initial blocks configuration for new content
   */
  const configureInitialBlocks = () => {
    config.blocks.initialBlocks = initialBlocks;
  };

  /**
   * Applies all modular configurations in sequence
   * @param baseConfig - The base configuration to extend
   * @returns The fully configured application config
   */
  const applyModularConfigurations = (baseConfig: ConfigType): ConfigType => {
    let currentConfig = baseConfig;
    currentConfig = configureSettings(currentConfig);
    currentConfig = configureWidgets(currentConfig);
    currentConfig = configureComponents(currentConfig);
    currentConfig = configureBlocks(currentConfig);
    currentConfig = configureViews(currentConfig);
    currentConfig = configureUtilities(currentConfig);
    return currentConfig;
  };

  // Execute configuration steps in order
  registerGlobalComponents();
  installCoreFunctionality();
  configureReducersAndTransformers();
  configureSlateToolbar();
  addSlateEditorExtensions();
  configureTableBlockExtensions();
  enableExperimentalFeatures();
  configureInitialBlocks();

  // Apply modular configurations with proper type handling
  return applyModularConfigurations(config);
};

export default applyConfig;
